import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Wordmark from "@/components/Wordmark";
import Footer from "@/components/Footer";
import { Check, ArrowLeft, Loader2, Trophy } from "lucide-react";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function JoinNow() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(12);
  const [tier, setTier] = useState("squad");
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", org: "", role: "", athletes: "", message: "" });
  const navigate = useNavigate();

  useEffect(() => { api.get("/membership/plans").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/access-requests", { ...f, tier, months });
      setSent(res.message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send · check your email address"); }
    finally { setBusy(false); }
  };

  const tiers = data?.tiers || [];

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="h-14 border-b border-border flex items-center gap-3 px-4 sm:px-8 shrink-0">
        <button data-testid="join-back" onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 hover:bg-surface transition-colors" aria-label="Back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Wordmark size="text-base" logoClass="w-5 h-5" />
      </header>

      <main className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-14" data-testid="join-now-page">
        <p className="text-[11px] tracking-[0.25em] uppercase font-bold text-brand-magenta flex items-center gap-2">
          <Trophy className="w-3.5 h-3.5" /> On a quest for winning
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-3 max-w-3xl">
          Bring your squad in.
        </h1>
        <p className="text-base text-muted-foreground mt-4 max-w-2xl">
          The demo runs on made-up athletes. A membership points the same engine at your real ones — their watch data,
          their sessions, their injuries, their match days. You keep the plan; we do the reading.
        </p>

        {/* term selector */}
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Commit for</span>
          <div className="inline-flex border border-foreground overflow-hidden" data-testid="join-term">
            {(data?.terms || []).map((t) => (
              <button key={t.months} data-testid={`join-term-${t.months}`} onClick={() => setMonths(t.months)}
                className={`px-3 py-1.5 text-xs transition-colors ${months === t.months ? "bg-foreground text-background" : "hover:bg-surface"}`}>
                {t.label}{t.off ? ` · ${t.off}% off` : ""}
              </button>
            ))}
          </div>
        </div>

        {/* tiers */}
        <div className="grid gap-4 sm:grid-cols-3 mt-6" data-testid="join-tiers">
          {tiers.map((t) => {
            const term = (t.terms || []).find((x) => x.months === months) || t.terms?.[0] || {};
            const active = tier === t.id;
            return (
              <button key={t.id} data-testid={`join-tier-${t.id}`} onClick={() => setTier(t.id)}
                className={`text-left border p-5 transition-colors ${active ? "border-foreground bg-surface" : "border-border hover:border-foreground"}`}>
                <div className="flex items-center gap-2">
                  <h2 className="font-heading font-black tracking-tight text-base md:text-lg">{t.name}</h2>
                  {t.badge && <span className="text-[10px] uppercase tracking-widest border border-brand-magenta text-brand-magenta px-1.5 py-0.5">{t.badge}</span>}
                  {active && <Check className="w-4 h-4 ml-auto" />}
                </div>
                <p className="mt-3 font-heading text-3xl font-black tabular-nums">{inr(term.monthly_inr)}<span className="text-sm font-normal text-muted-foreground"> /month</span></p>
                {months > 1 && (
                  <p className="text-[11px] text-muted-foreground mt-1 tabular-nums" data-testid={`join-total-${t.id}`}>
                    {inr(term.total_inr)} for {months} months · {inr(term.saving_inr)} less than paying monthly
                  </p>
                )}
                <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                  <li className="text-foreground tabular-nums">{(t.credits || 0).toLocaleString("en-IN")} credits every month</li>
                  <li>{t.athletes}</li>
                  <li>{t.for}</li>
                  <li className="tabular-nums">≈ ₹{term.per_credit} per credit</li>
                </ul>
              </button>
            );
          })}
        </div>
        {data?.note && <p className="text-xs text-muted-foreground mt-4 max-w-3xl leading-relaxed" data-testid="join-credit-note">{data.note}</p>}

        {/* what credits buy */}
        {data?.costs && (
          <div className="mt-6 border border-border p-4 text-xs" data-testid="join-costs">
            <p className="tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">What a credit is spent on</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-muted-foreground tabular-nums">
              <span>Video trace · {data.costs.video_chunk} per 2 min</span>
              <span>Voice note · {data.costs.audio_transcribe}</span>
              <span>Report or scan · {data.costs.file_extract}</span>
              <span>Deep plan search · {data.costs.peak_deep}</span>
              <span>Weekly report · {data.costs.report}</span>
            </div>
          </div>
        )}

        {/* request form */}
        <div className="mt-12 border-2 border-foreground" data-testid="join-form">
          <div className="px-5 py-3 border-b border-border">
            <h2 className="font-heading font-black tracking-tight text-base md:text-lg">Request access</h2>
            <p className="text-xs text-muted-foreground mt-1">
              We set up your squad with you — no card needed now. Tell us who you coach and we come back within a working day.
            </p>
          </div>
          {sent ? (
            <div className="p-6" data-testid="join-sent">
              <Check className="w-6 h-6 text-traffic-green" />
              <p className="text-sm mt-3">{sent}</p>
              <button data-testid="join-back-to-demo" onClick={() => navigate("/")}
                className="mt-5 text-sm border border-foreground px-4 py-2 hover:bg-surface transition-colors">Back to the demo</button>
            </div>
          ) : (
            <div className="p-5 grid gap-3 sm:grid-cols-2">
              <input data-testid="join-name" value={f.name} onChange={set("name")} placeholder="Your name"
                className="bg-background border border-border px-3 py-2 text-sm" />
              <input data-testid="join-email" value={f.email} onChange={set("email")} placeholder="Email" inputMode="email"
                className="bg-background border border-border px-3 py-2 text-sm" />
              <input data-testid="join-phone" value={f.phone} onChange={set("phone")} placeholder="Phone (optional)" inputMode="tel"
                className="bg-background border border-border px-3 py-2 text-sm" />
              <input data-testid="join-org" value={f.org} onChange={set("org")} placeholder="Team / academy"
                className="bg-background border border-border px-3 py-2 text-sm" />
              <input data-testid="join-role" value={f.role} onChange={set("role")} placeholder="Your role (head coach, physio…)"
                className="bg-background border border-border px-3 py-2 text-sm" />
              <input data-testid="join-athletes" value={f.athletes} onChange={set("athletes")} placeholder="How many athletes?"
                className="bg-background border border-border px-3 py-2 text-sm" />
              <textarea data-testid="join-message" value={f.message} onChange={set("message")} rows={3}
                placeholder="What do you want the system to fix first?"
                className="bg-background border border-border px-3 py-2 text-sm sm:col-span-2" />
              <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
                <button data-testid="join-submit" onClick={submit} disabled={busy}
                  className="bg-foreground text-background text-sm font-bold px-5 py-2.5 hover:bg-neutral-800 transition-colors flex items-center gap-2 disabled:opacity-40">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send request
                </button>
                <span className="text-[11px] text-muted-foreground">
                  Chosen: {tiers.find((t) => t.id === tier)?.name || "—"} · {months} month{months > 1 ? "s" : ""}
                </span>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
